import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import LinearProgress from '@material-ui/core/LinearProgress'
import IconButton from '@material-ui/core/IconButton'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import { Delete, Add, Remove } from '@material-ui/icons/'
import { useTransition, animated } from 'react-spring'
import styled from 'styled-components'
import { formatPrice } from '../../utils/helpers'
import StakeInput from './StakeInput'
import MessageTip from './MessageTip'

const AnimatedToolbar = styled(animated.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 99;
  width: 100%;
  background-color: ${props => props.theme.palette.background.paper};
  box-shadow: 0px -4px 8px 0px rgba(0, 0, 0, 0.2);
`
const Primary = styled.div`
  padding-top: ${props => props.theme.spacing(4)}px;
  padding-left: ${props => props.theme.spacing(4)}px;
  padding-right: ${props => props.theme.spacing(4)}px;
  display: flex;
`
const StakeContainer = styled.div`
  padding-bottom: ${props => props.theme.spacing(4)}px;
  padding-left: ${props => props.theme.spacing(4)}px;
  padding-right: ${props => props.theme.spacing(4)}px;
`
const Details = styled.div`
  flex: 1;
`
const Outcome = styled(Typography)`
  display: inline;
  &.MuiTypography-subtitle1 {
    font-weight: 600;
    line-height: 1.5rem;
  }
`
const Odds = styled(Typography)`
  display: inline;
  margin-left: ${props => props.theme.spacing(2)}px;
  &.MuiTypography-subtitle1 {
    font-weight: 600;
    line-height: 1.5rem;
  }
`
const Market = styled(Typography)`
  font-size: 0.875rem;
`
const MinimiseButton = styled(IconButton)`
  &.MuiIconButton-root {
    margin-top: -${props => props.theme.spacing(4)}px;
  }
`
const PlaceBetButton = styled(Button)`
  height: 48px;
`

const QuickBetInput = styled.div`
  display: flex;
  flex-wrap: nowrap;
`
const MinusButton = styled(Button)`
  background-color: ${props => props.theme.palette.oddsButton.default};
  margin-right: ${props => props.theme.spacing(2)}px;
  min-width: 36px;
  &.MuiButton-contained {
    color: ${props => props.theme.palette.primary.main};
    &:disabled {
      color: ${props => props.theme.palette.text.disabled};
    }
  }
  &:hover {
    background-color: ${props => props.theme.palette.oddsButton.hover};
  }
`
const AddButton = styled(Button)`
  background-color: ${props => props.theme.palette.oddsButton.default};
  margin-left: ${props => props.theme.spacing(2)}px;
  min-width: 36px;
  &.MuiButton-contained {
    color: ${props => props.theme.palette.primary.main};
    &:disabled {
      color: ${props => props.theme.palette.text.disabled};
    }
  }
  &:hover {
    background-color: ${props => props.theme.palette.oddsButton.hover};
  }
`
const MaxButton = styled(Button)`
  text-transform: uppercase;
  &.MuiButton-contained {
    color: ${props => props.theme.palette.primary.main};
    &:disabled {
      color: ${props => props.theme.palette.text.disabled};
    }
  }
`
const DeleteButton = styled(Button)`
  margin-left: auto;
  &.MuiButton-contained {
    color: ${props => props.theme.palette.primary.main};
    &:disabled {
      color: ${props => props.theme.palette.text.disabled};
    }
  }
`
const QuickbetInputLabel = styled.p`
  margin-bottom: 0;
  font-size: 0.778rem;
  margin-top: 4px;
`
const BetStake = styled.div`
  color: ${props => props.theme.palette.text.secondary};
  font-size: 0.788rem;
  margin-top: ${props => props.theme.spacing(1)}px;
`
const BetReturns = styled.div`
  color: ${props => props.theme.palette.text.secondary};
  font-size: 0.788rem;
  line-height: 1.5rem;
  margin-bottom: ${props => props.theme.spacing(4)}px;
`

const QuickBet = quickBetProps => {
  const {
    isOpen,
    bet,
    setHold,
    inputHandler,
    removeBet,
    clickHandler,
    handlePlaceBet,
    isUserLoggedIn,
    messageHub,
    dismissHandler, // Simple close of quick bet panel
    resetHandler, // Called after the panel has closed
    setScrollAllowance, // Need to call this in order to adjust body scroll height
  } = quickBetProps

  const { open, status, loading, successfulBet } = messageHub

  const { name, odds, marketTitle } = bet

  const maxStake = 1745

  // Capture a reference of the quickbet to obtain its height
  // We'll send this value to the setScrollAllowance function
  const quickbetBetRef = useRef(null)

  // returns and stake are undefined until a stake is entered
  const returns = bet.returns || 0
  const stake = parseFloat(bet.stake) || 0

  const QuickBetAnimation = useTransition(isOpen, null, {
    config: { tension: 250 },
    from: { transform: `translate3d(0,100%,0)` },
    enter: { transform: `translate3d(0,0,0)` },
    leave: { transform: `translate3d(0,100%,0)` },
    onRest: () => {
      if (status === `success`) {
        resetHandler()
      }
      if (isOpen) {
        setScrollAllowance(quickbetBetRef.current.clientHeight, false)
      }
    },
    onDestroyed: () => setScrollAllowance(0, true),
  })

  return QuickBetAnimation.map(
    ({ item, key, props }) =>
      item && (
        <AnimatedToolbar key={key} style={{ ...props }} ref={quickbetBetRef}>
          {loading && <LinearProgress variant="indeterminate" />}
          {open && <MessageTip messageHub={messageHub} />}
          {status !== `success` && (
            <>
              <Primary>
                <Details>
                  <Outcome variant="subtitle1">{name} @ </Outcome>
                  <Odds variant="subtitle1">{odds}</Odds>
                  <Market color="textSecondary">{marketTitle}</Market>
                </Details>
                <MinimiseButton
                  edge="end"
                  color="inherit"
                  onClick={() => setHold(true)}
                  aria-label="close"
                >
                  <KeyboardArrowDownIcon />
                </MinimiseButton>
              </Primary>
              <StakeContainer>
                <QuickbetInputLabel>Stake</QuickbetInputLabel>
                <QuickBetInput>
                  <MinusButton
                    variant="contained"
                    aria-label="minus-stake-bet"
                    onClick={() => clickHandler(stake - 1)}
                    disabled={stake < 1 || loading}
                  >
                    <Remove style={{ color: `inherit` }} />
                  </MinusButton>
                  <StakeInput
                    stake={stake}
                    inputHandler={inputHandler}
                    disabled={loading}
                  />
                  <AddButton
                    variant="contained"
                    aria-label="add-stake-bet"
                    onClick={() =>
                      clickHandler(stake <= maxStake - 1 ? stake + 1 : maxStake)
                    }
                    disabled={loading || stake === maxStake}
                  >
                    <Add />
                  </AddButton>
                  <MaxButton
                    variant="contained"
                    color="default"
                    aria-label="max-bet"
                    onClick={() => clickHandler(maxStake)}
                    style={{ marginLeft: `8px` }}
                    disabled={loading}
                  >
                    Max
                  </MaxButton>
                  <DeleteButton
                    variant="contained"
                    color="default"
                    onClick={removeBet}
                    aria-label="delete-bet"
                    style={{ marginLeft: `8px` }}
                    disabled={loading}
                  >
                    <Delete />
                  </DeleteButton>
                </QuickBetInput>
                <BetReturns>Returns: {formatPrice(returns * 100)}</BetReturns>
                <PlaceBetButton
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={!stake || !isUserLoggedIn || loading}
                  onClick={() => handlePlaceBet(parseFloat(stake))}
                >
                  {isUserLoggedIn ? `Place Bet` : `Please log in to place bet`}
                </PlaceBetButton>
              </StakeContainer>
            </>
          )}

          {status === `success` && (
            <>
              {Object.keys(successfulBet).map(index => {
                const successBet = successfulBet[index]

                return (
                  <ClickAwayListener onClickAway={dismissHandler} key={index}>
                    <Primary onClick={dismissHandler}>
                      <Details>
                        <Outcome variant="subtitle1">
                          {successBet.name} @{' '}
                        </Outcome>
                        <Odds variant="subtitle1">{successBet.odds}</Odds>
                        <Market color="textSecondary">
                          {successBet.marketTitle}
                        </Market>
                        <BetStake>
                          Stake: {formatPrice(successBet.stake)}
                        </BetStake>
                        <BetReturns>
                          Returns: {formatPrice(successBet.returns * 100)}
                        </BetReturns>
                      </Details>
                    </Primary>
                  </ClickAwayListener>
                )
              })}
            </>
          )}
        </AnimatedToolbar>
      )
  )
}

QuickBet.propTypes = {
  isOpen: PropTypes.bool,
  bet: PropTypes.object,
  marketTitle: PropTypes.string,
  odds: PropTypes.number,
  name: PropTypes.string,
  setHold: PropTypes.func,
  stake: PropTypes.string,
  deleteClick: PropTypes.func,
  format: PropTypes.string,
  clickHandler: PropTypes.func,
  messageHub: PropTypes.object,
}

export default QuickBet
